import React from 'react';
import {
  ArrowRight,
  CheckCircle2,
  FileCheck2,
  History,
  Network,
  ShieldAlert,
  Sparkles,
} from 'lucide-react';

interface ChangelogProps {
  setActiveTab: (tab: string) => void;
}

type Update = {
  label: string;
  title: string;
  summary: string;
  next: string;
  action?: string;
  actionLabel?: string;
  icon: React.ComponentType<{ className?: string }>;
  tone: string;
};

const updates: Update[] = [
  {
    label: 'Start here',
    title: 'Guided start for the full workflow',
    summary: 'A six-step walkthrough now shows how an agent moves from description and boundaries to validation, runtime decisions, and ongoing response.',
    next: 'Use it to understand the platform before changing anything.',
    action: 'guide',
    actionLabel: 'Open guided start',
    icon: Sparkles,
    tone: 'border-brand-200 bg-brand-50 text-brand-700 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300',
  },
  {
    label: 'For operators',
    title: 'Incident review is easier to understand',
    summary: 'Repeated alerts for the same action are grouped together, with the reason for the group visible before you expand the details.',
    next: 'Acknowledge records that are being investigated; use Approve & Close only when the response and evidence are complete.',
    action: 'review',
    actionLabel: 'Open risk review',
    icon: ShieldAlert,
    tone: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300',
  },
  {
    label: 'For governance teams',
    title: 'Profile history and rollback',
    summary: 'Profile changes are kept in history so an authorized operator can inspect an earlier version and restore it when a change was wrong.',
    next: 'Restores are recorded in the audit trail for later review.',
    action: 'profiles',
    actionLabel: 'Open profiles',
    icon: History,
    tone: 'border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/30 dark:bg-indigo-500/10 dark:text-indigo-300',
  },
  {
    label: 'For release owners',
    title: 'Validation decisions explain what happens next',
    summary: 'Validation now makes the difference between an accepted release, a rejected release, and a run that still needs human review explicit.',
    next: 'Only an approved passing run can move an agent toward a protected state.',
    action: 'validate',
    actionLabel: 'Open validation',
    icon: FileCheck2,
    tone: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300',
  },
  {
    label: 'For application owners',
    title: 'The connection path is visible before you integrate',
    summary: 'The integration lab shows where the application asks AgenticScale for a decision before it executes a sensitive action.',
    next: 'Use the public example to learn the flow, then sign in when you are ready to connect an organization application.',
    action: 'lab',
    actionLabel: 'Open integration lab',
    icon: Network,
    tone: 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300',
  },
  {
    label: 'For demos and evaluation',
    title: 'The public demo remains open',
    summary: 'You can browse the guided start and try the demonstration workspace without signing in. Sign-in is reserved for organization data and workspace changes.',
    next: 'This keeps the product easy to evaluate while preserving a clear path to organization-specific access.',
    action: 'dashboard',
    actionLabel: 'Explore the demo',
    icon: CheckCircle2,
    tone: 'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300',
  },
];

export const Changelog: React.FC<ChangelogProps> = ({ setActiveTab }) => (
  <div className="mx-auto w-full max-w-5xl xl:max-w-6xl 2xl:max-w-page space-y-7 animate-fade-in">
    <header className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-10">
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
          <Sparkles className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-600 dark:text-brand-300">What’s new</p>
          <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-slate-950 dark:text-white sm:text-4xl">Useful improvements to AgenticScale</h1>
          <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300 sm:text-base">A short, user-focused record of changes that affect how you explore, govern, and operate agents. Internal fixes and implementation details are intentionally left out.</p>
        </div>
      </div>
    </header>

    <section aria-labelledby="recent-updates-title">
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-600 dark:text-brand-300">Recent updates</p>
          <h2 id="recent-updates-title" className="mt-2 text-2xl font-extrabold text-slate-950 dark:text-white">Changes you can use now</h2>
        </div>
        <span className="hidden rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300 sm:inline-flex">{updates.length} highlights</span>
      </div>

      <div className="grid gap-4">
        {updates.map((update) => {
          const Icon = update.icon;
          return (
            <article key={update.title} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex min-w-0 items-start gap-4">
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${update.tone}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">{update.label}</p>
                    <h3 className="mt-1 text-lg font-extrabold text-slate-950 dark:text-white">{update.title}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">{update.summary}</p>
                  </div>
                </div>
                {update.action && (
                  <button type="button" onClick={() => setActiveTab(update.action!)} className="inline-flex shrink-0 items-center gap-2 self-start rounded-xl border border-brand-200 bg-brand-50 px-3 py-2 text-xs font-bold text-brand-700 transition hover:bg-brand-100 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300 dark:hover:bg-brand-500/20">
                    {update.actionLabel}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-5 text-slate-600 dark:border-slate-800 dark:bg-slate-950/50 dark:text-slate-400">
                <span className="font-bold text-slate-900 dark:text-white">What this means for you: </span>{update.next}
              </div>
            </article>
          );
        })}
      </div>
    </section>

    <p className="pb-2 text-center text-xs text-slate-500 dark:text-slate-400">We keep this page focused on changes that improve your work. Security fixes, dependency updates, and internal maintenance are not listed here.</p>
  </div>
);
